interface LoadingIconProps {
  size?: 'small' | 'medium' | 'large' | 'fullPage'
  message?: string
  className?: string
}

export default function LoadingIcon({ 
  size = 'medium', 
  message = 'Gearing up...',
  className = '' 
}: LoadingIconProps) {
  // Size configurations
  const sizeConfig = {
    small: {
      container: 'w-[60px] h-[60px]',
      gear: 'w-[50px] h-[50px]',
      dot: 'w-2 h-2',
      text: 'text-sm',
    },
    medium: {
      container: 'w-[120px] h-[120px]',
      gear: 'w-[100px] h-[100px]',
      dot: 'w-3 h-3',
      text: 'text-base',
    },
    large: {
      container: 'w-[200px] h-[200px]',
      gear: 'w-[170px] h-[170px]',
      dot: 'w-4 h-4',
      text: 'text-lg',
    },
    fullPage: {
      container: 'w-[250px] h-[250px]',
      gear: 'w-[220px] h-[220px]',
      dot: 'w-5 h-5',
      text: 'text-xl',
    },
  }

  const config = sizeConfig[size]

  return (
    <div className={`flex flex-col items-center justify-center gap-1 ${className}`}>
      {/* Gear Device Container */}
      <div className={`relative ${config.container} flex items-center justify-center`}>
        {/* PRND Gear Selector Pattern - Solid block matching brand guideline */}
        <svg
          className={`${config.gear}`}
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Solid dark teal background rectangle */}
          <rect
            x="5"
            y="5"
            width="90"
            height="90"
            rx="3"
            fill="#1e3a42"
          />
          
          {/* Light cream border */}
          <rect
            x="5"
            y="5"
            width="90"
            height="90"
            rx="3"
            fill="none"
            stroke="#fdfcf6"
            strokeWidth="1.5"
          />
          
          {/* PRND Letters in light cream */}
          <text
            x="18"
            y="28"
            fill="#fdfcf6"
            fontSize="11"
            fontFamily="Inter, sans-serif"
            fontWeight="600"
          >
            P
          </text>
          <text
            x="18"
            y="45"
            fill="#fdfcf6"
            fontSize="11"
            fontFamily="Inter, sans-serif"
            fontWeight="600"
          >
            R
          </text>
          <text
            x="18"
            y="62"
            fill="#fdfcf6"
            fontSize="11"
            fontFamily="Inter, sans-serif"
            fontWeight="600"
          >
            N
          </text>
          <text
            x="18"
            y="79"
            fill="#fdfcf6"
            fontSize="11"
            fontFamily="Inter, sans-serif"
            fontWeight="600"
          >
            D
          </text>
          
          {/* Gear selector path (stepped pattern) - solid yellow matching brand guideline exactly */}
          <path
            d="M 35 20 L 60 20 L 60 30 L 40 30 L 40 45 L 40 50 L 40 60 L 60 60 L 60 62 L 60 70 L 50 70 L 50 79 L 50 85"
            stroke="#f3dc6b"
            strokeWidth="7"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>

        {/* Animated Dot - Moving through PRND positions - white, no glow */}
        <div
          className={`absolute ${config.dot} rounded-full bg-white`}
          style={{
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            animation: 'gearShiftPRND 3s ease-in-out infinite',
          }}
        />
      </div>

      {/* Loading Message with animated dots */}
      {message && (
        <p className={`font-display font-medium ${config.text} text-text-primary-900 text-center`}>
          {message}
          <span className="inline-block">
            <span className="animate-[dot1_1.4s_ease-in-out_infinite]">.</span>
            <span className="animate-[dot2_1.4s_ease-in-out_infinite]">.</span>
            <span className="animate-[dot3_1.4s_ease-in-out_infinite]">.</span>
          </span>
        </p>
      )}
    </div>
  )
}
